'use client';

/**
 * Колокольчик оповещений.
 *
 * Разбор звонков работает, только если о плохом разговоре узнают в тот же
 * день, поэтому список сам обновляется раз в минуту, а не ждёт перезагрузки.
 *
 * Число берётся из `unreadCount` ответа и **не считается по загруженному
 * списку**: сервер считает его по всей базе, а список подрезан — иначе
 * счётчик врал бы ровно тогда, когда непрочитанного много.
 *
 * **Отказ запроса показывается отказом, а не пустым списком.** Раньше 403,
 * 404 и обрыв сети давали ровно то же «Оповещений нет», что и пустой ящик:
 * строки в базе лежали, а человек читал, что оповещений нет — и шёл искать
 * причину не там. Это худший вид ошибки, она не выглядит ошибкой.
 */

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { AlertTriangle, Bell, Check, Loader2 } from 'lucide-react';
import { ApiError } from '@/lib/api/client';
import { useNotifications } from '@/lib/api/hooks';
import { useMarkAllRead, useMarkNotificationRead } from '@/lib/api/mutations';
import { dateTimeCompact } from '@/lib/format';

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const notifications = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllRead();

  useEffect(() => {
    if (!open) return;
    const onClick = (event: MouseEvent) => {
      if (!panelRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const unread = notifications.data?.unreadCount ?? 0;
  const items = notifications.data?.items ?? [];
  /* Отказ, а не пустота: различать их обязательно — у них разные причины и
     разные действия. Пока есть прошлый ответ, список показываем его. */
  const failed = notifications.isError;

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen((value) => !value)}
        className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
        aria-label={unread > 0 ? `Оповещений непрочитано: ${unread}` : 'Оповещения'}
      >
        <Bell size={18} className="text-slate-500" />
        {/* Отказ виден и с закрытой панелью: иначе человек не узнает, что
            счётчик молчит не потому, что всё прочитано. */}
        {failed && unread === 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-500"
            title="Оповещения не удалось загрузить"
          />
        )}
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-red-500 text-on-fill text-[10px] font-bold flex items-center justify-center">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 max-h-[70vh] bg-white border border-slate-200 rounded-xl shadow-2xl z-50 flex flex-col animate-fadeIn">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-800 flex-1">Оповещения</h3>
            {unread > 0 && (
              <button
                onClick={() => void markAll.mutateAsync()}
                disabled={markAll.isPending}
                className="text-[11px] text-brand-light hover:text-brand-accent disabled:opacity-50"
              >
                Прочитать все
              </button>
            )}
          </div>

          <div className="overflow-y-auto">
            {notifications.isPending && (
              <div className="py-8 flex justify-center">
                <Loader2 size={18} className="animate-spin text-slate-300" />
              </div>
            )}

            {failed && (
              <div className="px-4 py-5 flex flex-col items-center gap-2 text-center">
                <AlertTriangle size={20} className="text-amber-500" />
                <p className="text-xs text-slate-600">Оповещения не удалось загрузить</p>
                <p className="text-[11px] text-slate-400 leading-relaxed">
                  {notifications.error instanceof ApiError
                    ? notifications.error.message
                    : 'Сервер не ответил'}
                  . Это не значит, что оповещений нет — мы просто не смогли их прочитать.
                </p>
                <button
                  onClick={() => void notifications.refetch()}
                  className="btn-secondary text-xs mt-1"
                >
                  Повторить
                </button>
              </div>
            )}

            {!notifications.isPending && !failed && items.length === 0 && (
              <p className="py-8 px-4 text-xs text-slate-400 text-center">
                Оповещений нет. Они приходят по правилам, которые заводит администратор, и только
                тем, кто на них подписался в «Моих оповещениях».
              </p>
            )}

            {items.map((item) => {
              const body = (
                <>
                  <div className="flex items-start gap-2">
                    {!item.readAt && (
                      <span className="w-1.5 h-1.5 rounded-full bg-brand-light mt-1.5 flex-shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium text-slate-800">{item.title}</div>
                      <p className="text-[11px] text-slate-600 mt-0.5 leading-relaxed">
                        {item.body}
                      </p>
                      <div className="text-[10px] font-mono text-slate-400 mt-1">
                        {dateTimeCompact(item.createdAt)}
                      </div>
                    </div>
                  </div>
                </>
              );

              return (
                <div
                  key={item.id}
                  className={`px-4 py-2.5 border-b border-slate-50 last:border-0 ${
                    item.readAt ? '' : 'bg-blue-50/40'
                  }`}
                >
                  {/* Оповещение без перехода бесполезно: человек прочитает
                      «плохой звонок» и не сможет его послушать. */}
                  {item.callId ? (
                    <Link
                      href={`/call-detail?id=${item.callId}`}
                      onClick={() => {
                        if (!item.readAt) void markRead.mutateAsync(item.id);
                        setOpen(false);
                      }}
                      className="block hover:opacity-80"
                    >
                      {body}
                    </Link>
                  ) : (
                    body
                  )}

                  {!item.readAt && (
                    <button
                      onClick={() => void markRead.mutateAsync(item.id)}
                      className="mt-1 inline-flex items-center gap-1 text-[10px] text-slate-400 hover:text-slate-600"
                    >
                      <Check size={10} /> прочитано
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
